import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTranslations } from "@/hooks/use-translations";
import { api } from "@/lib/api";

const money = (v: string) => {
	try {
		const n = BigInt(v);
		return `${n / 100n}.${(n % 100n).toString().padStart(2, "0")}`;
	} catch {
		return v;
	}
};

/**
 * The proposal being discussed in the chat, with the only way to approve it:
 * a click the server records. The agent can explain it, never accept it.
 */
export function MonitoringReviewBar({
	reviewId,
	onDismiss,
}: {
	reviewId: string;
	onDismiss: () => void;
}) {
	const t = useTranslations("notifications");
	const queryClient = useQueryClient();
	const review = useQuery({
		queryKey: ["monitoringReview", reviewId],
		queryFn: () => api.getMonitoringReview(reviewId),
		retry: false,
	});
	const answer = useMutation({
		mutationFn: (action: "approve" | "reject") =>
			api.answerMonitoringReview(reviewId, action, "chat"),
		onSuccess: (result) => {
			toast.success(
				result.status === "resolved"
					? t("saved")
					: result.status === "already_resolved"
						? t("status.resolved")
						: t("clarify"),
			);
			queryClient.invalidateQueries({ queryKey: ["monitoringReview", reviewId] });
			queryClient.invalidateQueries({ queryKey: ["notifications"] });
		},
		onError: () => toast.error(t("error")),
	});
	const r = review.data;
	return (
		<div
			role="status"
			className="mx-6 mb-2 rounded-xl border border-border-subtle bg-bg-surface p-3 text-sm text-text-primary"
		>
			<div className="flex items-start justify-between gap-3">
				<div>
					<strong>{t("chatReview.title")}</strong>
					{r && (
						<p className="mt-1 text-text-secondary">
							{t(`actions.${r.action}`)} · {t(r.level)} {r.metaId} ·{" "}
							{t(`reasons.${r.reasonCode}`)}
						</p>
					)}
					{r?.budget && (
						<p className="text-text-secondary">
							{t("chatReview.budget")}: {money(r.budget.before)} →{" "}
							{money(r.budget.after)} {r.currency ?? ""}
						</p>
					)}
					{r?.collectedAt && (
						<p className="text-text-tertiary">
							{t("chatReview.collectedAt")}: {new Date(r.collectedAt).toLocaleString()}
						</p>
					)}
					{review.isError && <p className="text-danger">{t("error")}</p>}
				</div>
				<button
					onClick={onDismiss}
					className="text-text-link underline underline-offset-2"
				>
					{t("close")}
				</button>
			</div>
			<div className="mt-2 flex flex-wrap items-center gap-2">
				{r?.status === "open" ? (
					<>
						{["PAUSE", "INCREASE"].includes(r.action) && (
							<Button
								size="sm"
								variant="secondary"
								disabled={answer.isPending}
								onClick={() => answer.mutate("approve")}
							>
								{t("approve")}
							</Button>
						)}
						<Button
							size="sm"
							variant="secondary"
							disabled={answer.isPending}
							onClick={() => answer.mutate("reject")}
						>
							{t("reject")}
						</Button>
					</>
				) : r ? (
					<Badge tone={r.status === "approved" ? "success" : "neutral"}>
						{t(`status.${r.status}`)}
					</Badge>
				) : null}
			</div>
		</div>
	);
}
